import { experimentsService } from '@wix/cashier-common/dist/src/utils/experimentsService';
import { FedopsLogger } from '@wix/fedops-logger';
import React, { useCallback, useRef, useState } from 'react';
import { Experiment } from '../../../types/Experiment';
import { InteractionName } from '../../../types/InteractionName';
import { getBiLogger } from '../../../utils/biLogger';
import { useTimeout } from '../../../utils/hooks';
import { t } from '../../../utils/i18n';
import { FramedModal } from '../FramedModal';
import { FramedModalApi, FramedModalProps } from '../FramedModal/controller';
import {
  squareVerifyBuyerModalConfig,
  squareVerifyBuyerModalName,
  squareVerifyBuyerModalPath,
} from './constants';
import {
  SquareBuyerVerificationDetails,
  SquareMessageFromFrame,
  SquareMessageToFrame,
  SquareVerifyBuyerMessageType,
} from './types';

export interface SquareVerifyBuyerModalProps {
  squareAppId: string;
  squareLocationId: string;
  cardToken: string;
  verificationDetails: SquareBuyerVerificationDetails;
  publicBaseUrl: string;
  locale: string;
  userId: string;
  widgetVersion?: string;
  fedopsLogger: FedopsLogger<InteractionName>;

  onComplete(buyerVerificationToken?: string): void;
  onError(error?: Error): void;
  onManualClose(): void;
}

export const SquareVerifyBuyerModalProvider: React.FC<
  SquareVerifyBuyerModalProps & {
    children(
      props: FramedModalProps
    ): React.ReactComponentElement<typeof FramedModal>;
  }
> = (props) => {
  const apiRef = useRef<FramedModalApi>();
  const handleApiInit = useCallback((api: FramedModalApi) => {
    apiRef.current = api;
  }, []);
  const [isDisplayed, setIsDisplayed] = useState<boolean>(false);

  const handleManualClose = useCallback(() => {
    if (isDisplayed) {
      getBiLogger().transaction3dsForm({ isFinished: false });
    }

    props.onManualClose();
  }, [isDisplayed, props.onManualClose]);

  const handleVerificationDisplayedTimeout = useCallback(() => {
    props.onError(new Error(`Square verification page wasn't shown in time`));
  }, [props.onError]);

  const clearTimeout = useTimeout({
    onTimeout: handleVerificationDisplayedTimeout,
    timeout: squareVerifyBuyerModalConfig.verificationDisplayedMaxTimeout,
  });

  const handleMessageFromProxyPage = useCallback(
    (data: SquareMessageFromFrame) => {
      switch (data.type) {
        case SquareVerifyBuyerMessageType.Ready:
          apiRef.current?.sendTransitiveMessageToFrame<SquareMessageToFrame>({
            type: SquareVerifyBuyerMessageType.VerifyBuyer,
            payload: {
              appId: props.squareAppId,
              locationId: props.squareLocationId,
              nonce: props.cardToken,
              verificationDetails: props.verificationDetails,
            },
          } as SquareMessageToFrame);
          break;
        case SquareVerifyBuyerMessageType.BuyerVerificationDisplayed:
          clearTimeout();
          getBiLogger().transaction3dsForm({ isOpened: true });
          setIsDisplayed(true);
          break;
        case SquareVerifyBuyerMessageType.BuyerVerified:
          getBiLogger().transaction3dsForm({ isFinished: true });
          props.onComplete(data.payload);
          break;
        case SquareVerifyBuyerMessageType.BuyerVerificationFailed:
          if (isDisplayed) {
            getBiLogger().transaction3dsForm({ isFinished: false });
          }

          props.onError(data.error);
          break;
        default:
      }
    },
    [props.onComplete, props.onError, isDisplayed]
  );

  const handleFrameMount = useCallback(() => {
    props.fedopsLogger.interactionEnded(InteractionName.SQUARE_3DS_MODAL_MOUNT);
  }, []);

  const missingRequiredProps: string[] = [
    !props.squareAppId && 'squareAppId',
    !props.squareLocationId && 'squareLocationId',
    !props.verificationDetails.amount && 'verificationDetails.amount',
    !props.verificationDetails.currencyCode &&
      'verificationDetails.currencyCode',
    !props.verificationDetails.billingContact.givenName &&
      'verificationDetails.billingContact.givenName',
  ].filter(Boolean);

  if (missingRequiredProps.length > 0) {
    props.onError(
      new Error(
        `SquareVerifyBuyerModal failed to render due to missing props: ${missingRequiredProps}`
      )
    );

    return null;
  }

  return props.children({
    modalName: squareVerifyBuyerModalName,
    url:
      props.publicBaseUrl +
      squareVerifyBuyerModalPath +
      (experimentsService.isExperimentEnabled(
        Experiment.UseSquareSandboxScripts
      )
        ? '?useSandbox=true'
        : ''),
    locale: props.locale,
    width: 700,
    height: 600,
    publicBaseUrl: props.publicBaseUrl,
    userId: props.userId,
    widgetVersion: props.widgetVersion,
    fedopsLogger: props.fedopsLogger,
    title: t('paymodal.title'),
    isCustomModalVisible: isDisplayed, // will be ignored if SDK modal is used
    onFrameMount: handleFrameMount,
    onClose: handleManualClose,
    onReadyTimeout: handleVerificationDisplayedTimeout,
    onTransitiveMessageFromFrame: handleMessageFromProxyPage,
    onApiInit: handleApiInit,
  });
};
SquareVerifyBuyerModalProvider.displayName = 'SquareVerifyBuyerModalProvider';

export const SquareVerifyBuyerModal: React.FC<SquareVerifyBuyerModalProps> = (
  props
) => (
  <SquareVerifyBuyerModalProvider {...props}>
    {(renderProps) => <FramedModal {...renderProps} />}
  </SquareVerifyBuyerModalProvider>
);
SquareVerifyBuyerModal.displayName = 'SquareVerifyBuyerModal';
